// React-friendly reimplementation of the Social Fellows site behavior script.
// Returns a cleanup function that removes listeners / injected nodes.
export function initSiteBehavior() {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const timeouts = [];
  const st = (fn, ms) => { const id = setTimeout(fn, ms); timeouts.push(id); return id; };
  let io = null;
  const cleanups = [];

  /* sticky header shadow */
  const hdr = document.querySelector('.hdr');
  const onScroll = () => { if (hdr) hdr.classList.toggle('stuck', window.scrollY > 6); };
  if (hdr) {
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', onScroll));
  }

  /* announcement bar height -> mobile nav offset */
  const annc = document.querySelector('.annc');
  if (annc) document.documentElement.style.setProperty('--anh', annc.offsetHeight + 'px');

  /* mobile nav */
  const burger = document.querySelector('.burger');
  const nav = document.querySelector('.nav');
  if (burger && nav) {
    const onBurger = () => {
      const open = nav.classList.toggle('open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    const onNavClick = (e) => {
      if (e.target.closest('a')) {
        nav.classList.remove('open');
        burger.setAttribute('aria-expanded', 'false');
      }
    };
    burger.addEventListener('click', onBurger);
    nav.addEventListener('click', onNavClick);
    cleanups.push(() => burger.removeEventListener('click', onBurger));
    cleanups.push(() => nav.removeEventListener('click', onNavClick));
  }

  /* hero statement — reveal in word groups */
  const stmt = document.querySelector('.stmt');
  if (stmt) {
    const words = stmt.querySelectorAll('.w');
    if (reduce) { words.forEach((w) => w.classList.add('on')); }
    else { words.forEach((w, i) => st(() => w.classList.add('on'), 120 + i * 55)); }
  }

  /* scroll reveal + one-shot triggers for trajectory points and ledger bars */
  const targets = document.querySelectorAll('.rv, .tp, .lrow');
  if (reduce || !('IntersectionObserver' in window)) {
    targets.forEach((n) => n.classList.add('in'));
  } else {
    io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const el = en.target;
        const sibs = el.parentNode ? Array.prototype.indexOf.call(el.parentNode.children, el) : 0;
        st(() => el.classList.add('in'), el.classList.contains('rv') ? 0 : sibs * 110);
        io.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -6% 0px' });
    targets.forEach((n) => io.observe(n));
  }

  /* forms: careers form -> application API (with CV); others -> mailto */
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

  const enhanceCareersForm = (form) => {
    if (form.dataset.enhanced) return;
    form.dataset.enhanced = '1';

    // build the CV upload field
    const wrap = document.createElement('div');
    const label = document.createElement('label');
    label.setAttribute('for', 'c-cv');
    label.textContent = 'Attach CV / resume (PDF or DOC, max 10MB) — optional';
    const input = document.createElement('input');
    input.type = 'file';
    input.id = 'c-cv';
    input.name = 'cv';
    input.accept = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    wrap.appendChild(label);
    wrap.appendChild(input);

    const status = document.createElement('p');
    status.style.cssText = 'font-family:var(--mono);font-size:.74rem;letter-spacing:.04em;margin:4px 0 0';

    const submitBtn = form.querySelector('button[type="submit"], button');
    if (submitBtn) form.insertBefore(wrap, submitBtn);
    else form.appendChild(wrap);

    const onSubmit = async (e) => {
      e.preventDefault();
      status.style.color = 'var(--grey)';
      status.textContent = 'Sending…';
      if (!status.parentNode && submitBtn) submitBtn.insertAdjacentElement('afterend', status);

      const get = (n) => { const el = form.elements[n]; return el ? el.value : ''; };
      const fd = new FormData();
      fd.append('name', get('Name'));
      fd.append('field', get('Field'));
      fd.append('city', get('City'));
      fd.append('posting', get('Field posting'));
      fd.append('message', get('message'));
      if (input.files && input.files[0]) {
        if (input.files[0].size > 10 * 1024 * 1024) {
          status.style.color = '#c0392b';
          status.textContent = 'CV exceeds the 10MB limit.';
          return;
        }
        fd.append('cv', input.files[0]);
      }
      try {
        const res = await fetch(API_BASE + '/applications', { method: 'POST', body: fd });
        if (!res.ok) throw new Error('failed');
        status.style.color = 'var(--accent)';
        status.textContent = 'Thank you — your interest has been registered.';
        form.reset();
      } catch (err) {
        status.style.color = '#c0392b';
        status.textContent = 'Something went wrong. Please email socialfellows.in@gmail.com.';
      }
    };
    form.addEventListener('submit', onSubmit);
    cleanups.push(() => form.removeEventListener('submit', onSubmit));
  };

  const mailtoHandler = (form) => (e) => {
    e.preventDefault();
    const d = new FormData(form), lines = [];
    d.forEach((v, k) => { if (k !== 'message') lines.push(k + ': ' + v); });
    lines.push('', d.get('message') || '');
    window.location.href = 'mailto:socialfellows.in@gmail.com'
      + '?subject=' + encodeURIComponent(form.getAttribute('data-subject') || 'Website enquiry')
      + '&body=' + encodeURIComponent(lines.join('\n'));
  };

  document.querySelectorAll('form[data-mailto]').forEach((form) => {
    const subject = form.getAttribute('data-subject') || '';
    if (/Social Fellow/i.test(subject)) {
      enhanceCareersForm(form);
    } else {
      const h = mailtoHandler(form);
      form.addEventListener('submit', h);
      cleanups.push(() => form.removeEventListener('submit', h));
    }
  });

  /* scroll progress (single instance) */
  let bar = document.querySelector('.prog-bar');
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'prog-bar';
    document.body.appendChild(bar);
  }
  const tick = () => {
    const h = document.documentElement.scrollHeight - window.innerHeight;
    bar.style.width = (h > 0 ? (window.scrollY / h) * 100 : 0) + '%';
  };
  tick();
  window.addEventListener('scroll', tick, { passive: true });
  window.addEventListener('resize', tick);
  cleanups.push(() => window.removeEventListener('scroll', tick));
  cleanups.push(() => window.removeEventListener('resize', tick));
  cleanups.push(() => { if (bar && bar.parentNode) bar.parentNode.removeChild(bar); });

  /* hero field of Fellows — fills left to right */
  const figs = document.querySelectorAll('.figfield svg');
  if (figs.length) {
    if (reduce) { figs.forEach((f) => f.classList.add('on')); }
    else { figs.forEach((f, i) => st(() => f.classList.add('on'), 520 + i * 42)); }
  }

  /* duplicate marquee content so the loop is seamless (guarded) */
  document.querySelectorAll('.mq-track').forEach((t) => {
    if (!t.dataset.dup) { t.innerHTML += t.innerHTML; t.dataset.dup = '1'; }
  });

  return function cleanup() {
    timeouts.forEach(clearTimeout);
    if (io) io.disconnect();
    cleanups.forEach((fn) => {
      try {
        fn();
      } catch (error) {
        console.error('Error during site behavior cleanup:', error);
      }
    });
  };
}
